import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Wrench, Download, Eye, Tag, Search, Monitor, Server, Cloud } from 'lucide-react';
import { useData } from '../context/DataContext';

const ToolsPage = () => {
  const { tools, trackPageView } = useData();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  useEffect(() => {
    trackPageView('tools');
  }, [trackPageView]);

  const categories = ['All', ...new Set(tools.map(t => t.category).filter(Boolean))];

  const filteredTools = tools.filter(tool => {
    const matchesSearch = !searchQuery.trim() || 
      (tool.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (tool.description || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (tool.category || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || tool.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getPlatformIcon = (platform) => {
    const p = (platform || '').toLowerCase();
    if (p.includes('cloud') || p.includes('azure')) return Cloud;
    if (p.includes('server') || p.includes('linux')) return Server;
    return Monitor;
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Wrench size={32} color="#17a2b8" />
          Tools & Utilities
        </h1>
        <p className="page-description">
          Download powerful DBA tools and utilities to streamline your SQL Server administration tasks. 
          Each tool includes detailed instructions, documentation, and scripts to help you get started.
        </p>
      </div>

      {/* Search and Filter Bar */}
      <div className="section-card" style={{ 
        display: 'flex', gap: '15px', marginBottom: '30px', flexWrap: 'wrap', 
        alignItems: 'center', padding: '20px'
      }}>
        <div style={{ flex: 1, minWidth: '250px', position: 'relative' }}>
          <Search size={18} className="tool-search-icon" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
          <input
            type="text"
            placeholder="Search tools..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="form-input"
            style={{ paddingLeft: '40px' }}
          />
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`tab-button ${selectedCategory === cat ? 'active' : ''}`}
              style={{ padding: '8px 16px', fontSize: '0.85rem' }}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div style={{ 
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
        gap: '15px', marginBottom: '30px' 
      }}>
        <div className="stat-card" style={{ textAlign: 'center' }}>
          <Wrench size={28} color="#17a2b8" />
          <div className="stat-number">{tools.length}</div>
          <div className="stat-label">Total Tools</div>
        </div>
        <div className="stat-card" style={{ textAlign: 'center' }}>
          <Download size={28} color="#28a745" />
          <div className="stat-number">{tools.reduce((sum, t) => sum + (parseInt(t.downloads) || 0), 0)}</div>
          <div className="stat-label">Total Downloads</div>
        </div>
        <div className="stat-card" style={{ textAlign: 'center' }}>
          <Eye size={28} color="#ffc107" />
          <div className="stat-number">{tools.reduce((sum, t) => sum + (parseInt(t.views) || 0), 0)}</div>
          <div className="stat-label">Total Views</div>
        </div>
      </div>

      {/* Tools Grid */}
      {filteredTools.length === 0 ? (
        <div className="section-card" style={{ textAlign: 'center', padding: '60px 20px' }}>
          <Wrench size={48} className="tool-empty-icon" style={{ marginBottom: '15px' }} />
          <h3 className="tool-empty-title">No tools found</h3>
          <p className="tool-empty-text">
            {searchQuery ? 'Try adjusting your search query.' : 'Tools & utilities will appear here once added.'}
          </p>
        </div>
      ) : (
        <div className="tools-grid">
          {filteredTools.map(tool => {
            const PlatformIcon = getPlatformIcon(tool.platform);
            return (
              <Link 
                key={tool.id} 
                to={`/tool/${tool.id}`} 
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <div className="section-card tool-card" style={{ 
                  height: '100%', transition: 'transform 0.2s, box-shadow 0.2s',
                  cursor: 'pointer', position: 'relative', overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '';
                }}
                >
                  {/* Category Badge */}
                  <div className="tool-category-badge" style={{ 
                    position: 'absolute', top: '15px', right: '15px', 
                    padding: '3px 10px', borderRadius: '12px', 
                    fontSize: '0.75rem', fontWeight: '600' 
                  }}>
                    {tool.category}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px', marginBottom: '15px' }}>
                    <div style={{ 
                      background: 'linear-gradient(135deg, #17a2b8, #138496)', 
                      borderRadius: '12px', padding: '12px', 
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      minWidth: '48px', minHeight: '48px'
                    }}>
                      <Wrench size={24} color="white" />
                    </div>
                    <div style={{ flex: 1, paddingRight: '70px' }}>
                      <h3 className="tool-card-title" style={{ margin: '0 0 5px 0', fontSize: '1.1rem' }}>
                        {tool.title}
                      </h3>
                      {tool.version && (
                        <span className="tool-version-badge" style={{ 
                          fontSize: '0.75rem', padding: '2px 8px', borderRadius: '4px' 
                        }}>
                          v{tool.version}
                        </span>
                      )}
                    </div>
                  </div>

                  <p className="tool-card-description" style={{ 
                    fontSize: '0.9rem', lineHeight: '1.5', 
                    marginBottom: '15px', 
                    display: '-webkit-box', WebkitLineClamp: 3, 
                    WebkitBoxOrient: 'vertical', overflow: 'hidden' 
                  }}>
                    {tool.description}
                  </p>

                  <div className="tool-card-footer" style={{ 
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    paddingTop: '12px', marginTop: 'auto'
                  }}>
                    <div className="tool-card-meta" style={{ display: 'flex', gap: '15px', fontSize: '0.8rem' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <PlatformIcon size={14} />
                        {tool.platform || 'Windows'}
                      </span>
                      {tool.file_size && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Download size={14} />
                          {tool.file_size}
                        </span>
                      )}
                    </div>
                    <div className="tool-card-meta" style={{ display: 'flex', gap: '12px', fontSize: '0.8rem' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Eye size={14} />
                        {tool.views || 0}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Download size={14} />
                        {tool.downloads || 0}
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ToolsPage;
